import {
    Badge,
    BlockStack,
    Box,
    Card,
    InlineStack,
    Link,
    StatCard,
    Typography,
} from '@pv-frontend/ui-components-web';
import { vaultListHref } from './vault-list-filter';

/**
 * Plain view model for the platform summary — deliberately decoupled from the
 * Relay generated types so this presentational component is unit-testable without
 * a Relay harness. The screen maps `platformSummary` query data into it.
 */
export interface PlanBreakdownItemView {
    planName: string;
    /** Every subscription on the plan, complimentary ones included. */
    subscriptionCount: number;
    /** How many of `subscriptionCount` are complimentary grants rather than sold. */
    complimentaryCount: number;
}

export interface PlatformSummaryView {
    totalVaults: number;
    totalAccounts: number;
    freeSubscriptions: number;
    paidSubscriptions: number;
    complimentarySubscriptions: number;
    planBreakdown: readonly PlanBreakdownItemView[];
}

const fmt = (n: number) => n.toLocaleString();

export type SubscriptionBucket = 'paying' | 'free' | 'complimentary';

export interface SubscriptionBucketView {
    bucket: SubscriptionBucket;
    label: string;
    /** What lands in the bucket, in the server's terms (entitlement, not store status). */
    description: string;
    count: number;
}

export interface SubscriptionBreakdown {
    /** The whole subscription population — the server puts each subscription in exactly one bucket. */
    total: number;
    /** Always all three buckets, in a fixed order — a zero is reported, never dropped. */
    buckets: readonly SubscriptionBucketView[];
}

/**
 * Maps the server's three disjoint buckets into what the summary renders. Rendered as reported: which
 * bucket a subscription belongs to is decided server-side (by entitlement), never re-derived here.
 */
export function subscriptionBreakdown(summary: PlatformSummaryView): SubscriptionBreakdown {
    const buckets: SubscriptionBucketView[] = [
        {
            bucket: 'paying',
            label: 'Paying',
            description: 'Paying for Pro right now',
            count: summary.paidSubscriptions,
        },
        {
            bucket: 'free',
            label: 'Free',
            description: 'Not on Pro, including lapsed purchases and lapsed grants',
            count: summary.freeSubscriptions,
        },
        {
            bucket: 'complimentary',
            label: 'Complimentary',
            description: 'Pro granted by an operator, still within its term',
            count: summary.complimentarySubscriptions,
        },
    ];
    return {
        total: buckets.reduce((sum, b) => sum + b.count, 0),
        buckets,
    };
}

/** Summary dashboard: headline stat cards plus the per-plan subscription breakdown. */
export function TenantSummary({ summary }: { summary: PlatformSummaryView }) {
    const breakdown = subscriptionBreakdown(summary);

    return (
        <BlockStack gap="400">
            <InlineStack gap="400" blockAlign="stretch">
                <StatCard label="Total vaults" value={fmt(summary.totalVaults)} icon="home" />
                <StatCard label="Total accounts" value={fmt(summary.totalAccounts)} icon="users" />
                <StatCard
                    label="Subscriptions"
                    value={fmt(breakdown.total)}
                    caption={breakdown.buckets
                        .map((bucket) => `${fmt(bucket.count)} ${bucket.label.toLowerCase()}`)
                        .join(' · ')}
                    icon="credit-card"
                />
            </InlineStack>

            <Card padding="0">
                <Box paddingInline="400" paddingBlock="300">
                    <InlineStack align="space-between" blockAlign="center" gap="300">
                        <Typography variant="body-sm" fontWeight="medium" tone="text-secondary">
                            Standing
                        </Typography>
                        <Typography variant="body-sm" fontWeight="medium" tone="text-secondary">
                            Subscriptions
                        </Typography>
                    </InlineStack>
                </Box>
                {breakdown.buckets.map((bucket) => (
                    <Box
                        key={bucket.bucket}
                        testID={`subscription-bucket-${bucket.bucket}`}
                        paddingInline="400"
                        paddingBlock="300"
                        borderColor="border-card"
                        borderBlockStartWidth="0165"
                    >
                        <InlineStack align="space-between" blockAlign="center" gap="300">
                            <BlockStack gap="050">
                                <Typography variant="body-md" fontWeight="medium">
                                    {bucket.label}
                                </Typography>
                                <Typography variant="body-sm" tone="text-secondary">
                                    {bucket.description}
                                </Typography>
                            </BlockStack>
                            {bucket.bucket === 'complimentary' ? (
                                // Always a link, zero included: the filtered list says so honestly.
                                <Link href={vaultListHref('complimentary')}>{fmt(bucket.count)}</Link>
                            ) : (
                                <Badge tone="neutral">{fmt(bucket.count)}</Badge>
                            )}
                        </InlineStack>
                    </Box>
                ))}
            </Card>

            <Card padding="0">
                <Box paddingInline="400" paddingBlock="300">
                    <InlineStack align="space-between" blockAlign="center" gap="300">
                        <Typography variant="body-sm" fontWeight="medium" tone="text-secondary">
                            Plan
                        </Typography>
                        <Typography variant="body-sm" fontWeight="medium" tone="text-secondary">
                            Subscriptions
                        </Typography>
                    </InlineStack>
                </Box>
                {summary.planBreakdown.length === 0 ? (
                    <Box paddingInline="400" paddingBlock="300" borderColor="border-card" borderBlockStartWidth="0165">
                        <Typography tone="text-secondary">No subscriptions yet.</Typography>
                    </Box>
                ) : (
                    summary.planBreakdown.map((plan) => (
                        <Box
                            key={plan.planName}
                            testID={`plan-${plan.planName}`}
                            paddingInline="400"
                            paddingBlock="300"
                            borderColor="border-card"
                            borderBlockStartWidth="0165"
                        >
                            <InlineStack align="space-between" blockAlign="center" gap="300">
                                <Typography variant="body-md" fontWeight="medium">
                                    {plan.planName}
                                </Typography>
                                <InlineStack gap="200" blockAlign="center">
                                    <Typography variant="body-sm" tone="text-secondary">
                                        {`${fmt(plan.complimentaryCount)} complimentary`}
                                    </Typography>
                                    <Badge tone="neutral">{fmt(plan.subscriptionCount)}</Badge>
                                </InlineStack>
                            </InlineStack>
                        </Box>
                    ))
                )}
            </Card>
        </BlockStack>
    );
}
